import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language;
  const langLabel = currentLang === 'pt' ? 'PT' : currentLang === 'en' ? 'EN' : 'DE';

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCategoriesOpen(false);
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const changeLang = (lang: string) => {
    i18n.changeLanguage(lang);
    setLangOpen(false);
  };

  const scrollToSection = (id: string) => {
    setMobileOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goHome = () => {
    setMobileOpen(false);
    navigate('/');
  };

  const goProducts = () => {
    setMobileOpen(false);
    navigate('/products');
  };

  const catName = (cat: typeof categories[0]) => {
    if (currentLang === 'pt') return cat.name;
    if (currentLang === 'en') return cat.nameEn;
    return cat.nameDe;
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-foreground-200/60 shadow-sm shadow-foreground-100/40">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 text-foreground-900 hover:text-primary-600 transition-colors">
            <span className="font-heading text-xl md:text-2xl font-bold tracking-tight">cssbuybr.living</span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <button onClick={goHome} className="px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-600 rounded-md transition-colors whitespace-nowrap">
              {t('nav_home')}
            </button>

            <button onClick={goProducts} className={`px-3 py-2 text-sm font-semibold hover:text-primary-600 rounded-md transition-colors whitespace-nowrap ${location.pathname === '/products' ? 'text-primary-700 bg-primary-50' : 'text-foreground-800'}`}>
              Products
            </button>

            <div ref={catRef} className="relative">
              <button
                onClick={() => setCategoriesOpen(!categoriesOpen)}
                className="px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-600 rounded-md transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${categoriesOpen ? 'rotate-180' : ''}`} />
              </button>
              {categoriesOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-background-200 rounded-xl shadow-xl overflow-hidden max-h-[70vh] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-primary-50 transition-colors group"
                      onClick={() => setCategoriesOpen(false)}
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-100 border border-background-200">
                        <img src={cat.image} alt={catName(cat)} className="w-full h-full object-cover" />
                      </div>
                      <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-700">{catName(cat)}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => { setMobileOpen(false); navigate('/article/cssbuy-buying-guide'); }}
              className="px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-600 rounded-md transition-colors whitespace-nowrap"
            >
              {t('nav_shopping_guide')}
            </button>

            <button onClick={() => scrollToSection('articles')} className="px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-600 rounded-md transition-colors whitespace-nowrap">
              {t('nav_guides')}
            </button>
            <button onClick={() => scrollToSection('how-it-works')} className="px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-600 rounded-md transition-colors whitespace-nowrap">
              {t('nav_safety')}
            </button>
            <a href="/faq" className="px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-600 rounded-md transition-colors whitespace-nowrap">
              {t('nav_faq')}
            </a>
          </div>

          {/* Language + Mobile Toggle */}
          <div className="flex items-center gap-2">
            <div ref={langRef} className="relative hidden md:block">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="px-3 py-1.5 text-xs font-semibold border border-background-300 rounded-md text-foreground-800 hover:bg-background-100 transition-colors flex items-center gap-1"
              >
                {langLabel}
                <i className="ri-arrow-down-s-line w-3.5 h-3.5 flex items-center justify-center" />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-1 w-20 bg-white border border-background-200 rounded-lg shadow-xl overflow-hidden">
                  <button onClick={() => changeLang('pt')} className={`block w-full text-left px-4 py-2 text-sm transition-colors ${currentLang === 'pt' ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-foreground-700 hover:bg-background-100'}`}>PT</button>
                  <button onClick={() => changeLang('en')} className={`block w-full text-left px-4 py-2 text-sm transition-colors ${currentLang === 'en' ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-foreground-700 hover:bg-background-100'}`}>EN</button>
                  <button onClick={() => changeLang('de')} className={`block w-full text-left px-4 py-2 text-sm transition-colors ${currentLang === 'de' ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-foreground-700 hover:bg-background-100'}`}>DE</button>
                </div>
              )}
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 text-foreground-800 hover:text-primary-600 rounded-md transition-colors"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} w-5 h-5 flex items-center justify-center text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-white px-4 pb-6 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-1 pt-3">
            <button onClick={goHome} className="flex items-center gap-2 text-left px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-md transition-colors">
              <i className="ri-home-4-line w-4 h-4 flex items-center justify-center text-primary-500" />
              {t('nav_home')}
            </button>

            <button onClick={goProducts} className={`flex items-center gap-2 text-left px-3 py-2.5 text-sm font-semibold hover:bg-background-100 rounded-md transition-colors ${location.pathname === '/products' ? 'text-primary-700 bg-primary-50' : 'text-foreground-800'}`}>
              <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center text-primary-500" />
              Products
            </button>

            <button
              onClick={() => { setMobileOpen(false); navigate('/article/cssbuy-buying-guide'); }}
              className="flex items-center gap-2 text-left px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-md transition-colors"
            >
              <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center text-primary-500" />
              {t('nav_shopping_guide')}
            </button>

            {/* Categories */}
            <div className="mt-2">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide px-3 py-2">{t('nav_categories')}</p>
              <div className="grid grid-cols-2 gap-2 px-3">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 bg-background-50 border border-background-200 rounded-lg p-2 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 bg-background-100 border border-background-200">
                      <img src={cat.image} alt={catName(cat)} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xs font-medium text-foreground-700 leading-tight">{catName(cat)}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200 my-3" />

            <button onClick={() => { setMobileOpen(false); scrollToSection('articles'); }} className="flex items-center gap-2 text-left px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-md transition-colors">
              <i className="ri-book-open-line w-4 h-4 flex items-center justify-center text-primary-500" />
              {t('nav_guides')}
            </button>
            <button onClick={() => { setMobileOpen(false); scrollToSection('how-it-works'); }} className="flex items-center gap-2 text-left px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-md transition-colors">
              <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center text-primary-500" />
              {t('nav_safety')}
            </button>
            <a href="/faq" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-md transition-colors">
              <i className="ri-question-line w-4 h-4 flex items-center justify-center text-primary-500" />
              {t('nav_faq')}
            </a>

            <div className="flex gap-2 px-3 pt-3">
              <button onClick={() => { changeLang('pt'); setMobileOpen(false); }} className={`px-3 py-1.5 text-xs font-semibold border rounded-md transition-colors ${currentLang === 'pt' ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-background-300 text-foreground-700'}`}>PT</button>
              <button onClick={() => { changeLang('en'); setMobileOpen(false); }} className={`px-3 py-1.5 text-xs font-semibold border rounded-md transition-colors ${currentLang === 'en' ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-background-300 text-foreground-700'}`}>EN</button>
              <button onClick={() => { changeLang('de'); setMobileOpen(false); }} className={`px-3 py-1.5 text-xs font-semibold border rounded-md transition-colors ${currentLang === 'de' ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-background-300 text-foreground-700'}`}>DE</button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}